const CHILD_KEYS = ['days_of_week', 'start_times', 'end_times', 'start_dates', 'end_date'];
const START_DATES_KEYS = ['start_dates'];
const ENUM_KEYS = ['subjects', 'parent_group', 'title'];

/**
 * @typedef {"must" | "must_not"} MustOrMustNot
 */


/**
 * Converts filter object into elasticsearch format
 * @param {Object} obj - key/value filter object
 * e.g. {'grades': ['1st Grade', '2nd Grade'] }
 *      {'subjects': 'Math'}
 *      {'price_cents': {'from': 0.01}}
 */
/* eslint-disable complexity */
function mapFilterObject(obj) {
  for(const [key, value] of Object.entries(obj)) {
    if (key === 'any') {
      return { bool: { should: value.map(mapFilterObject), minimum_should_match: 1 } };
    } else {
      const field = mapFieldName(key);
      if (Object.keys(value).includes('from')
          || Object.keys(value).includes('to')
          || Object.keys(value).includes('gt')
          || Object.keys(value).includes('gte')
          || Object.keys(value).includes('lt')
          || Object.keys(value).includes('lte')) {
        return { range: { [field]: value } };
      }
      if (Array.isArray(value)) {
        if (field === 'subjects.enum') {
          // include the Related Subjects field too (equivalent to "subjects OR related_subjects")
          return {
            bool: {
              should: [
                { terms: { 'subjects.enum': value } },
                { terms: { 'related_subjects.enum': value }}
              ],
              minimum_should_match: 1
            }
          };
        }
        return { terms: { [field]: value } };
      }
      if (key === 'exists' && value?.field === 'topic_ids') {
        // whitelist to exclude parent items with topic_ids when show_topics is true
        return { [field]: value };
      }
      return { term: { [field]: value } };
    }
  }
}
/* eslint-enable complexity */

/**
 * Maps local field names to elasticsearch required variants
 * @param {String} input - The input field name
 */
function mapFieldName(input) {
  if(ENUM_KEYS.includes(input))
    return `${input}.enum`;

  if(START_DATES_KEYS.includes(input))
    return 'child_items.start_date';

  if(CHILD_KEYS.includes(input))
    return `child_items.${input}`;

  return input;
}

/**
 * Receives the filters chosen from the course list and maps them to a format
 * understood by the elastic-search API.
 */
export default class ElasticSearchFiltersFactory {
  constructor(filters) {
    this.filters = filters;
    this.allFilters = filters.all || [];
    this.noneFilters = filters.none || [];
  }

  /**
   * @returns {Array} The formatted filters for elastic-search API
   */
  build() {
    if(this.isEmpty()) { return []; }
    this.validateDates(this.allFilters);

    //Iterate all the filters supplied and separate them into parent
    //and child groups as child filters need to be nested in the query
    //payload
    const allFilters = this.sortFilters(this.allFilters);
    const noneFilters = this.sortFilters(this.noneFilters);
    return [{ bool: this.buildFilters(allFilters, noneFilters) }];
  }

  /**
   * Returns a JSON-formatted string of the built filters
   * @returns {String}
   */
  toJSON() {
    return JSON.stringify(this.build());
  }

  /**
   * @returns {true|false}
   */
  isEmpty() {
    return this.allFilters.length === 0;
  }

  /**
   * Function to validate the start_times and end_times if either of those
   * are rolled over to the next or previous day due to time zones.
   *
   * If the from or to time for either start or end times are rolled over
   * to next or previous day, the times will be added to the noneFilters and
   * will be removed from allFilters(switching from must to must_not).
   *
   * if both from and to time for either start or end times are rolled over
   * to next or previous day, the times will be forced to reset to 1970-01-01.
   */
  /* eslint-disable complexity */
  validateDates(filterArray) {
    const updatedFilterArray = [];
    filterArray.forEach((current) => {
      const field = Object.keys(current)[0];
      if (field === 'start_times' || field === 'end_times') {
        const gte = current[field].from;
        const lte = current[field].to;
        const startTimeCorrect = gte.indexOf('1970-01-01') >= 0;
        const endTimeCorrect = lte.indexOf('1970-01-01') >= 0;

        if ((startTimeCorrect && !endTimeCorrect) || (!startTimeCorrect && endTimeCorrect)) {
          // For example, set time filter to 1pm to 7pm Pacific
          current[field] = {'gt': gte, 'lt': lte};
          current[field].gt = '1970-01-01' + 'T' + lte.split('T')[1];
          current[field].lt = '1970-01-01' + 'T' + gte.split('T')[1];
          this.noneFilters.push(current);
        } else {
          // For example, set time filter to 1am-5am Pacific or 5pm-9pm Pacific
          current[field] = {'gte': gte, 'lte': lte};
          current[field].gte = '1970-01-01' + 'T' + gte.split('T')[1];
          current[field].lte = '1970-01-01' + 'T' + lte.split('T')[1];
          updatedFilterArray.push(current);
        }
      } else {
        return updatedFilterArray.push(current);
      }
    });

    this.allFilters = updatedFilterArray;
  }
  /* eslint-enable complexity */

  /**
   * To enable searching of child offerings within a course,
   * we need to nest them in the query payload.
   * because range filters on the any key may come in mixed parent and
   * child, we need to split them appart before running them through
   * the conversion function to elasticsearch format
   * @see test/scripts/item_search_test.js:192
   */
  sortFilters(filterArray) {
    if (!Array.isArray(filterArray)) {
      filterArray = Object.entries(filterArray).map(([key, value]) => ({[key]: value}));
    }

    return filterArray.reduce((memo, current) => {
      const field = Object.keys(current)[0];

      if (field === 'any') {
        const { parents, children } = this.sortFilters(current.any);
        this.pushToAnyFilters(parents, memo.parents);
        this.pushToAnyFilters(children, memo.children);

      } else if (CHILD_KEYS.includes(field)) {
        memo.children.push(current);

      } else {
        memo.parents.push(current);
      }

      return memo;
    }, { parents: [], children: [] });
  }

  /**
   * Adds an { any: ... } object to an array with the contents of the source array,
   * if it is not blank.
   * @param {Array} sourceArray - The array you want to append
   * @param {Array} toArray - The array you want to push an 'any' field to
   */
  pushToAnyFilters(sourceArray, toArray) {
    if(sourceArray && sourceArray.length) {
      toArray.push({ any: sourceArray });
    }
  }

  /**
   * Parses and processes nested filters on child objects
   * @param {Object} allFilters - The object of 'all' filters
   * @param {Object} noneFilters - The object of 'none' filters (items to exclude)
   */
  buildFilters(allFilters, noneFilters) {
    const items = {}, childItems = {};
    this.setMappedProperty(items, 'must', allFilters.parents);
    this.setMappedProperty(items, 'must_not', noneFilters.parents);
    this.setMappedProperty(childItems, 'must', allFilters.children);
    this.setMappedProperty(childItems, 'must_not', noneFilters.children);

    if (Object.keys(childItems).length > 0) {
      items.must.push(
        {
          nested: {
            path: 'child_items',
            inner_hits: { size: 100},
            query: {
              bool: childItems
            }
          }
        }
      );
    }
    return items;
  }

  /**
   * Maps an array of filters with mapFilterObject and then appends it to either
   * the 'must' or 'must_not' property of the given object.
   *
   * @param {Object} obj - The object to set the property
   * @param {MustOrMustNot} property - The property name (e.g. must, must_not)
   * @param {Array} sourceArray - The array to map and append to the object
   */
  setMappedProperty(obj, property, sourceArray) {
    if(sourceArray && sourceArray.length) {
      obj[property] = sourceArray.map(mapFilterObject);
    }
  }
}
